import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { apiJson, isMissingRoute } from '../../api/client';
import { ApiError } from '../../api/errors';
import { type MoveBody, type TransitionChecks, useMoveTask } from '../../api/tasks';
import type { Task } from '../../api/types';
import { Button } from '../../components/ui/Button';
import { DateField } from '../../components/ui/DatePicker';
import { Dialog } from '../../components/ui/Dialog';
import { Checkbox, Field, FormAlert, Input, Textarea } from '../../components/ui/Field';
import { UserChipPicker } from './QuickAdd';
import { useUserMap } from '../../api/org';
import { useShell } from '../../app/shell-context';

/*
 * Переміщення на етап з умовами (модуль automation): перед переміщенням — перевірка переходу.
 * Якщо етап має контрольні питання чи формулу коментаря — компактний діалог; відповіді йдуть у checks
 * тіла POST /api/v1/tasks/{id}/move (checks.answers, checks.comment, checks.result). Без модуля — звичайне переміщення.
 */

type Evaluation = {
  allowed: boolean;
  projectId: string;
  toSectionId: string | null;
  errors: { field: string; message: string }[];
  questions: { id: string; text: string; help?: string; answered: boolean }[];
  /** optional — складник, який сервер не вимагає (напр. залучена особа в модулі legal). */
  commentFormula: { required: boolean; parts: { key: string; label: string; filled: boolean; optional?: boolean }[] } | null;
};

export type GuardedMove = { task: Task; body: MoveBody; sectionName?: string };
export type MoveOutcome = 'moved' | 'cancelled' | 'failed';

type Pending = GuardedMove & { ev: Evaluation; resolve: (o: MoveOutcome, task?: Task, error?: unknown) => void };

type Ctx = (move: GuardedMove) => Promise<{ outcome: MoveOutcome; task?: Task; error?: unknown }>;

const GuardContext = createContext<Ctx | null>(null);

export function useGuardedMove(): Ctx {
  const ctx = useContext(GuardContext);
  if (!ctx) throw new Error('TransitionGuardProvider відсутній');
  return ctx;
}

const PART_KEYS: Record<string, string> = {
  причина: 'reason',
  'очікувана дія': 'expectedAction',
  'залучена особа': 'involvedPerson',
  'контрольний строк': 'controlDate',
  'наступний крок': 'nextStep',
};

/** Відмова сервера (422 з полями checks.*) → перелік вимог для діалогу переходу. */
function evaluationFromErrors(e: ApiError, body: MoveBody): Evaluation | null {
  const checks = e.errors.filter((x) => x.field.startsWith('checks.'));
  if (!checks.length) return null;
  const questions = checks
    .filter((x) => x.field.startsWith('checks.answers.'))
    .map((x) => ({
      id: x.field.slice('checks.answers.'.length),
      text: x.message
        .replace(/^(Контрольне питання|Потрібна відповідь «так»):\s*/, '')
        .replace(/\s*—\s*потрібна відповідь «так»\.?$/, ''),
      answered: false,
    }));
  const commentErr = checks.find((x) => x.field === 'checks.comment');
  const parts = commentErr
    ? (commentErr.message.split(':')[1] ?? '')
        .split(',')
        .map((p) => p.trim().toLocaleLowerCase('uk'))
        .map((label) => ({
          key: PART_KEYS[label] ?? '',
          label: label.charAt(0).toLocaleUpperCase('uk') + label.slice(1),
          filled: false,
          // Залучену особу legal не вимагає (працівник або зовнішня особа — за потреби).
          optional: PART_KEYS[label] === 'involvedPerson',
        }))
        .filter((p) => p.key)
    : [];
  const others = checks.filter((x) => !x.field.startsWith('checks.answers.') && x.field !== 'checks.comment');
  return {
    allowed: false,
    projectId: body.projectId,
    toSectionId: body.sectionId,
    errors: others.map((x) => ({ field: x.field, message: x.message })),
    questions,
    commentFormula: parts.length ? { required: true, parts } : null,
  };
}

export function TransitionGuardProvider({ children }: { children: ReactNode }) {
  const move = useMoveTask();
  const [pending, setPending] = useState<Pending | null>(null);
  const moveRef = useRef(move);
  moveRef.current = move;

  const run: Ctx = useCallback(async ({ task, body, sectionName }) => {
    let ev: Evaluation | null = null;
    if (body.sectionId) {
      try {
        ev = await apiJson<Evaluation>('POST', `/api/v1/automation/tasks/${task.id}/transition-check`, { projectId: body.projectId, toSectionId: body.sectionId });
      } catch (e) {
        // Модуль не підключено (404 маршруту) або перевірка недоступна — сервер однаково перевірить правила під час переміщення.
        void isMissingRoute(e);
        ev = null;
      }
    }
    const needsDialog = !!ev && (ev.questions.length > 0 || !!ev.commentFormula || (!ev.allowed && ev.errors.length > 0));
    if (!needsDialog) {
      try {
        const t = await moveRef.current.mutateAsync({ id: task.id, body });
        return { outcome: 'moved' as const, task: t };
      } catch (error) {
        // Умови переходу, які перевіряють інші модулі (напр. legal), приходять як 422 з полями checks.* — показуємо той самий діалог.
        const fromErrors = error instanceof ApiError && error.status === 422 ? evaluationFromErrors(error, body) : null;
        if (!fromErrors) return { outcome: 'failed' as const, error };
        ev = fromErrors;
      }
    }
    return new Promise((resolve) => {
      setPending({ task, body, sectionName, ev: ev!, resolve: (outcome, t, error) => resolve({ outcome, task: t, error }) });
    });
  }, []);

  const value = useMemo(() => run, [run]);

  return (
    <GuardContext.Provider value={value}>
      {children}
      {pending && (
        <TransitionDialog
          pending={pending}
          onDone={(outcome, t) => {
            pending.resolve(outcome, t);
            setPending(null);
          }}
        />
      )}
    </GuardContext.Provider>
  );
}

const PART_FIELD: Record<string, 'textarea' | 'input' | 'date' | 'person'> = {
  reason: 'textarea',
  expectedAction: 'textarea',
  involvedPerson: 'person',
  controlDate: 'date',
  nextStep: 'textarea',
};

const CONTROL_PARTS = [
  { key: 'submittedOn', label: 'Дата подання', kind: 'date' },
  { key: 'authority', label: 'Орган чи адресат', kind: 'input' },
  { key: 'expectedBy', label: 'Очікуваний строк рішення', kind: 'date' },
  { key: 'controlMethod', label: 'Спосіб контролю', kind: 'input' },
] as const;
type ControlKey = (typeof CONTROL_PARTS)[number]['key'];

/** Поля checks, які заповнюються в діалозі; решта — умови, що виконуються в картці задачі. */
const ANSWERABLE = ['checks.answers', 'checks.comment', 'checks.result', 'checks.legalResult', 'checks.plannedDueOn', 'checks.control'];
const isAnswerable = (field: string) => field === 'result' || ANSWERABLE.some((k) => field === k || field.startsWith(`${k}.`));

function TransitionDialog({ pending, onDone }: { pending: Pending; onDone: (o: MoveOutcome, task?: Task) => void }) {
  const { task, body, ev, sectionName } = pending;
  const move = useMoveTask();
  const users = useUserMap();
  const shell = useShell();
  const location = useLocation();
  const [params] = useSearchParams();
  const [open, setOpen] = useState(true);
  const [answers, setAnswers] = useState<Record<string, boolean>>(() => Object.fromEntries(ev.questions.map((q) => [q.id, q.answered])));
  const [comment, setComment] = useState<NonNullable<TransitionChecks['comment']>>({});
  const [result, setResult] = useState('');
  const [legalResult, setLegalResult] = useState('');
  const [plannedDueOn, setPlannedDueOn] = useState<string | null>(null);
  const [control, setControl] = useState<Partial<Record<ControlKey, string>>>({});
  // Після першої спроби помилки перераховуються наживо: виправлене поле одразу втрачає позначку.
  const [tried, setTried] = useState(false);

  const needs = (k: string) => ev.errors.some((e) => e.field === k || e.field.startsWith(`${k}.`));
  const needsResult = needs('checks.result') || ev.errors.some((e) => e.field === 'result');
  const needsLegal = needs('checks.legalResult');
  const needsPlanned = needs('checks.plannedDueOn');
  const needsControl = needs('checks.control');
  const blocking = ev.errors.filter((e) => !isAnswerable(e.field));
  // Перехід, не передбачений процесом чи роллю, у діалозі не виправити — лише пояснення.
  const impossible = blocking.some((e) => e.field === 'checks.toStage');
  // Умови поза діалогом (підзадача, шлюз готовності, обов’язкове поле) сервер однаково не пропустить:
  // не пропонуємо «Перемістити», а показуємо, що виконати в картці й що знадобиться потім.
  const fillable = blocking.length === 0;
  const cardOpen = params.get('task') === task.id || location.pathname === `/tasks/${task.id}`;
  const serverErr = move.error instanceof ApiError ? move.error : null;
  const srvErrors = serverErr?.fieldErrors() ?? {};

  const parts = ev.commentFormula?.parts ?? [];
  const rendered = new Set<string>([
    ...ev.questions.map((q) => `checks.answers.${q.id}`),
    ...parts.flatMap((p) =>
      p.key === 'involvedPerson' ? ['checks.comment.involvedPerson', 'checks.comment.involvedPersonId', 'checks.comment.involvedUserId'] : [`checks.comment.${p.key}`],
    ),
    ...(needsResult ? ['checks.result'] : []),
    ...(needsLegal ? ['checks.legalResult'] : []),
    ...(needsPlanned ? ['checks.plannedDueOn'] : []),
    ...(needsControl ? CONTROL_PARTS.map((c) => `checks.control.${c.key}`) : []),
  ]);
  const validate = () => {
    const errs: Record<string, string> = {};
    for (const q of ev.questions) if (!answers[q.id]) errs[`checks.answers.${q.id}`] = 'Підтвердьте, щоб перемістити';
    if (ev.commentFormula?.required)
      for (const p of parts) {
        if (p.optional) continue;
        if (p.key === 'involvedPerson') {
          if (!comment.involvedPersonId && !comment.involvedPerson?.trim()) errs['checks.comment.involvedPerson'] = 'Оберіть працівника або вкажіть зовнішню особу';
          continue;
        }
        const v = (comment as Record<string, string | undefined>)[p.key]?.trim() ?? '';
        if (!v) errs[`checks.comment.${p.key}`] = 'Заповніть за формулою коментаря';
        else if (PART_FIELD[p.key] === 'textarea' && v.length < 3) errs[`checks.comment.${p.key}`] = 'Щонайменше 3 символи';
      }
    if (needsResult && !result.trim()) errs['checks.result'] = 'Опишіть результат етапу';
    if (needsLegal && legalResult.trim().length < 10) errs['checks.legalResult'] = 'Опишіть юридичний результат (щонайменше 10 символів)';
    if (needsPlanned && !plannedDueOn) errs['checks.plannedDueOn'] = 'Оберіть новий плановий строк';
    if (needsControl) for (const c of CONTROL_PARTS) if (!control[c.key]?.trim()) errs[`checks.control.${c.key}`] = 'Обов’язково для контролю рішення';
    return errs;
  };
  const local = tried ? validate() : {};
  const err = (f: string) => local[f] ?? (rendered.has(f) ? srvErrors[f] : undefined);
  const personErr = err('checks.comment.involvedPerson') ?? err('checks.comment.involvedPersonId') ?? err('checks.comment.involvedUserId');

  const later = [
    ev.questions.length ? `відповіді на контрольні питання (${ev.questions.length})` : null,
    parts.length ? `коментар за формулою: ${parts.map((p) => p.label.toLocaleLowerCase('uk')).join(', ')}` : null,
    needsResult ? 'результат етапу' : null,
    needsLegal ? 'юридичний результат' : null,
    needsPlanned ? 'новий плановий строк' : null,
    needsControl ? 'дані подання: дата, орган, очікуваний строк і спосіб контролю' : null,
  ].filter((x): x is string => !!x);

  const close = (o: MoveOutcome, t?: Task) => {
    setOpen(false);
    onDone(o, t);
  };

  const submit = () => {
    setTried(true);
    if (Object.keys(validate()).length) return;
    const checks: TransitionChecks = {};
    if (ev.questions.length) checks.answers = Object.fromEntries(ev.questions.map((q) => [q.id, true]));
    if (ev.commentFormula) {
      const c = Object.fromEntries(Object.entries(comment).filter(([, v]) => v && String(v).trim())) as NonNullable<TransitionChecks['comment']>;
      checks.comment = c;
    }
    if (needsResult) checks.result = result.trim();
    if (needsLegal) checks.legalResult = legalResult.trim();
    if (needsPlanned && plannedDueOn) checks.plannedDueOn = plannedDueOn;
    if (needsControl) checks.control = Object.fromEntries(CONTROL_PARTS.map((c) => [c.key, control[c.key]?.trim()])) as TransitionChecks['control'];
    move.mutate({ id: task.id, body: { ...body, checks } }, { onSuccess: (t) => close('moved', t) });
  };

  return (
    <Dialog
      open={open}
      onClose={() => close('cancelled')}
      title={`Перехід на етап «${sectionName ?? 'новий етап'}»`}
      description={`${task.key}: ${task.title}`}
      onSubmit={fillable ? submit : undefined}
      footer={
        fillable ? (
          <>
            <Button variant="ghost" onClick={() => close('cancelled')}>
              Скасувати
            </Button>
            <Button type="submit" variant="primary" loading={move.isPending}>
              Перемістити
            </Button>
          </>
        ) : (
          <>
            <Button variant={impossible || cardOpen ? 'primary' : 'ghost'} onClick={() => close('cancelled')}>
              Зрозуміло
            </Button>
            {!impossible && !cardOpen && (
              <Button
                variant="primary"
                onClick={() => {
                  close('cancelled');
                  shell.openTask(task.id);
                }}
              >
                Відкрити картку
              </Button>
            )}
          </>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {blocking.length > 0 && (
          <FormAlert title={impossible ? 'Перехід недоступний' : 'Умови етапу ще не виконано'} items={blocking.map((e) => e.message)}>
            {!impossible && <p className="mt-1">Виконайте їх у картці задачі й повторіть переміщення.</p>}
          </FormAlert>
        )}
        {!fillable && !impossible && later.length > 0 && (
          <div className="text-meta text-fg-2">
            <p className="font-medium text-fg">Під час переходу також знадобляться</p>
            <ul className="mt-1 list-disc pl-5">
              {later.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
          </div>
        )}
        {fillable && serverErr && <FormAlert detail={serverErr.detail} items={serverErr.messagesExcept([...rendered].filter((f) => srvErrors[f]))} />}
        {fillable && ev.questions.length > 0 && (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-meta font-semibold text-fg">Контрольні питання</legend>
            {ev.questions.map((q) => {
              const e = err(`checks.answers.${q.id}`);
              return (
                <div key={q.id}>
                  <Checkbox checked={!!answers[q.id]} onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))} label={q.text} description={q.help} />
                  {e && <p className="mt-0.5 pl-6 text-caption text-danger-fg">{e}</p>}
                </div>
              );
            })}
          </fieldset>
        )}
        {fillable && ev.commentFormula && (
          <fieldset className="flex flex-col gap-3">
            <legend className="mb-1 text-meta font-semibold text-fg">
              Коментар за формулою{ev.commentFormula.required ? '' : ' (за бажанням)'}
            </legend>
            {parts.map((p) => {
              const kind = PART_FIELD[p.key] ?? 'input';
              const required = ev.commentFormula?.required && !p.optional;
              if (kind === 'person') {
                const u = comment.involvedPersonId ? users.get(comment.involvedPersonId) : undefined;
                return (
                  <fieldset key={p.key} className="flex min-w-0 flex-col gap-1.5">
                    <legend className="mb-1.5 text-meta font-medium text-fg">
                      {p.label}
                      {!required && <span className="ml-1.5 text-caption font-normal text-fg-3">за потреби</span>}
                    </legend>
                    <div className="flex flex-wrap items-center gap-2">
                      <UserChipPicker
                        value={comment.involvedPersonId ?? null}
                        onChange={(id) => setComment((c) => ({ ...c, involvedPersonId: id }))}
                        label="Залучена особа"
                        pickLabel="Обрати залученого працівника"
                      />
                      <span className="text-body text-fg-2">{u?.name ?? 'Працівник'}</span>
                      <Input
                        value={comment.involvedPerson ?? ''}
                        placeholder="або зовнішня: клієнт, орган, контрагент"
                        onChange={(e) => setComment((c) => ({ ...c, involvedPerson: e.target.value }))}
                        aria-label={`${p.label}: зовнішня особа`}
                        aria-invalid={personErr ? true : undefined}
                        className="min-w-48 flex-1"
                      />
                    </div>
                    {personErr && (
                      <p role="alert" className="text-caption text-danger-fg">
                        {personErr}
                      </p>
                    )}
                  </fieldset>
                );
              }
              if (kind === 'date')
                return (
                  <Field key={p.key} label={p.label} required={required} error={err(`checks.comment.${p.key}`)}>
                    <DateField value={comment.controlDate ?? null} onChange={(v) => setComment((c) => ({ ...c, controlDate: v ?? undefined }))} />
                  </Field>
                );
              const value = (comment as Record<string, string | undefined>)[p.key] ?? '';
              return (
                <Field key={p.key} label={p.label} required={required} error={err(`checks.comment.${p.key}`)}>
                  {kind === 'textarea' ? (
                    <Textarea rows={2} value={value} onChange={(e) => setComment((c) => ({ ...c, [p.key]: e.target.value }))} />
                  ) : (
                    <Input value={value} onChange={(e) => setComment((c) => ({ ...c, [p.key]: e.target.value }))} />
                  )}
                </Field>
              );
            })}
          </fieldset>
        )}
        {fillable && needsResult && (
          <Field label="Результат етапу" required error={err('checks.result')}>
            <Textarea rows={3} value={result} onChange={(e) => setResult(e.target.value)} />
          </Field>
        )}
        {fillable && needsLegal && (
          <Field label="Юридичний результат" required error={err('checks.legalResult')} hint="Що саме досягнуто для клієнта: документ, рішення, реєстрація.">
            <Textarea rows={3} value={legalResult} onChange={(e) => setLegalResult(e.target.value)} />
          </Field>
        )}
        {fillable && needsPlanned && (
          <Field label="Новий плановий строк" required error={err('checks.plannedDueOn')} hint="Не в минулому й не пізніше за базовий строк замовлення.">
            <DateField value={plannedDueOn} onChange={setPlannedDueOn} />
          </Field>
        )}
        {fillable && needsControl && (
          <fieldset className="flex flex-col gap-3">
            <legend className="mb-1 text-meta font-semibold text-fg">Подання для контролю рішення</legend>
            {CONTROL_PARTS.map((c) => (
              <Field key={c.key} label={c.label} required error={err(`checks.control.${c.key}`)}>
                {c.kind === 'date' ? (
                  <DateField value={control[c.key] ?? null} onChange={(v) => setControl((x) => ({ ...x, [c.key]: v ?? undefined }))} />
                ) : (
                  <Input value={control[c.key] ?? ''} onChange={(e) => setControl((x) => ({ ...x, [c.key]: e.target.value }))} />
                )}
              </Field>
            ))}
          </fieldset>
        )}
      </div>
    </Dialog>
  );
}
